/**
 * @file CorsFetchInterceptor.js
 * @description Interceptor global para la API `fetch` que aplica de forma transparente
 *              las mismas estrategias de proxy CORS avanzadas ya disponibles para Axios.
 *              De este modo garantizamos que TODAS las peticiones cross-origin, sin
 *              importar si usan Axios o `fetch`, pasen por el proxy cuando sea necesario.
 *
 * Diseñado siguiendo principios SOLID y el patrón Decorator, el interceptor envuelve la
 * implementación original de `window.fetch`, inyectando la lógica de `CorsProxyService`
 * antes de delegar la llamada real. Mantiene compatibilidad total con la firma estándar
 * de `fetch` y permite su des-registro limpio para evitar fugas de memoria en tests.
 *
 * @version 1.0.0
 * @author Nubelytics Engineering
 */

import corsProxyService from './CorsProxyService';
import { isSafeDomain } from './CorsProxyConfig';

// Telemetría (se utiliza el mismo servicio que los interceptores Axios)
// Importación defendida para evitar fallos en entornos donde no exista aún el servicio
let telemetryService = null;
let TelemetryEvents = {};
try {
  // Ruta relativa: infrastructure/cors -> services/TelemetryService
  // Nota: usamos require relativo controlado dentro del mono-repo; la validación ESLint estándar se omite
  const telemetryModule = require('../../services/TelemetryService');
  telemetryService = telemetryModule.default || telemetryModule;
  TelemetryEvents = telemetryModule.TelemetryEvents || {};
} catch (_) {
  // Entorno de pruebas o build preliminar: la telemetría no es crítica
  telemetryService = { trackEvent: () => {} };
}

// Garantizar que exista el evento para registro
if (!TelemetryEvents.FETCH_INTERCEPTOR_REGISTERED) {
  TelemetryEvents.FETCH_INTERCEPTOR_REGISTERED = 'fetch_interceptor_registered';
}

/**
 * Interceptor / Decorador para la función global `fetch`.
 * Encapsula la lógica de proxy sin modificar la firma pública.
 */
class CorsFetchInterceptor {
  /**
   * @param {CorsProxyService} proxyService Servicio de proxy a utilizar. Se inyecta para facilitar tests.
   */
  constructor(proxyService = corsProxyService) {
    if (!proxyService || typeof proxyService.processRequest !== 'function') {
      throw new Error('[CorsFetchInterceptor] Se requiere una instancia válida de CorsProxyService');
    }
    this.proxyService = proxyService;
    this._originalFetch = null;
    this._registered = false;
  }

  /**
   * Registra el interceptor reemplazando `window.fetch`.
   * Invocar solo una vez. Subsecuentes llamadas devuelven `false`.
   * @returns {boolean} `true` si se registró, `false` si ya estaba registrado.
   */
  register() {
    // Evitar doble parcheo: si ya existe indicador _corsPatched asumimos que otro módulo aplicó un wrapper
    if (this._registered || typeof window === 'undefined' || typeof window.fetch !== 'function' || window.fetch._corsPatched) {
      return false;
    }

    // Conservar referencia para restaurar más tarde
    this._originalFetch = window.fetch.bind(window);

    // Referencia local para uso dentro del wrapper
    const proxyService = this.proxyService;
    const originalFetch = this._originalFetch;
    const self = this;

    // Función wrapper que aplica el proxy antes de invocar la original
    // eslint-disable-next-line func-names
    // Wrapper que aplica el proxy antes de invocar la implementación original
    // eslint-disable-next-line func-names
    window.fetch = async function (resource, initOptions = {}) {
      try {
        // Determinar URL (puede ser Request o string)
        const originalUrl = typeof resource === 'string' ? resource : resource?.url?.toString() || '';

        // Si es un dominio seguro, no interceptamos en absoluto para evitar efectos colaterales (p.ej. Firestore)
        if (isSafeDomain(originalUrl)) {
          return await originalFetch(resource, initOptions);
        }

        let options = initOptions || {};

        // Aplicar estrategias de proxy (incluye cabeceras adicionales)
        const { url: maybeProxiedUrl, options: extraOptions } = proxyService.processRequest(originalUrl, options);

        // Fusionar cabeceras respetando precedencia de las devueltas por la estrategia
        const mergedHeaders = self._mergeHeaders(options.headers, extraOptions.headers);
        options = { ...options, ...extraOptions, headers: mergedHeaders };

        // Invocar fetch real
        return await originalFetch(maybeProxiedUrl, options);
      } catch (error) {
        // Fallback: en raros casos la estrategia anterior no añadió proxy y obtenemos error CORS
        // Intentamos forzar NubelyticsProxyStrategy como último recurso.
        try {
          const forcedUrl = proxyService.applyNubelyticsProxyStrategy(resource, initOptions);
          if (forcedUrl && forcedUrl !== resource) {
            return await originalFetch(forcedUrl, initOptions);
          }
        } catch (_) {
          // Ignorar, devolver error original
        }
        throw error;
      }
          // Nota: cualquier propiedad enumerable se perdería en la reasignación; añadimos marca para futuros módulos
      // Evitar parches duplicados por otros módulos como CorsProxyRegistry
    };

    // Marcar como parcheado para que otros módulos lo detecten
    window.fetch._corsPatched = true;
    window.fetch._corsPatchedBy = 'CorsFetchInterceptor';

    this._registered = true;
    // Registrar evento en telemetría
    telemetryService.trackEvent?.(TelemetryEvents.FETCH_INTERCEPTOR_REGISTERED, {
      timestamp: Date.now(),
    });
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[CorsFetchInterceptor] Interceptor global de fetch registrado');
    }
    return true;
  }

  /**
   * Restaura la implementación original de `fetch`.
   * @returns {boolean} `true` si se restauró, `false` si no estaba registrado.
   */
  unregister() {
    if (!this._registered || !this._originalFetch) {
      return false;
    }
    window.fetch = this._originalFetch;
    this._registered = false;
    return true;
  }

  /**
   * Combina dos colecciones de cabeceras (Objeto plano o `Headers`) priorizando las segundas.
   * @private
   * @param {Headers|Object} h1 Cabeceras base
   * @param {Headers|Object} h2 Cabeceras a sobre-escribir
   * @returns {Object} Objeto simple con la fusión de cabeceras
   */
  _mergeHeaders(h1, h2) {
    // Normalizar a objeto plano
    const toObject = (h) => {
      if (!h) return {};
      if (h instanceof Headers) {
        return Object.fromEntries(h.entries());
      }
      return { ...h };
    };
    return { ...toObject(h1), ...toObject(h2) };
  }
}

export default CorsFetchInterceptor;
